import React, { useState, useMemo } from "react"; // Removed useEffect for now
import { useSelector } from "react-redux";
import Skeleton from "react-loading-skeleton";
import { useGetCategoriesQuery } from "../../product/api/productTaxonomyApi";
import {
  buildCategoryTree,
  CategoryWithChildren,
  hasCategoryProducts,
} from "../../product/helpers/productsCategoryHelpers";
import { selectIsCategoryMegaMenuOpen } from "../headerUiSlice"; // Use selector
import type { TransformedWPCategory } from "../../product/types/productTaxonomyApiTypes";

const CategoryMegaMenu: React.FC = () => {
  // --- State and Selectors ---
  const isMegaMenuOpen = useSelector(selectIsCategoryMegaMenuOpen); // Use selector to get open state
  const { data: categoriesData, isLoading: categoriesLoading } =
    useGetCategoriesQuery();
  const [hoveredParentId, setHoveredParentId] = useState<number | null>(null);

  // --- Memoized Data Processing ---
  const categoriesById: Record<number, TransformedWPCategory> | undefined =
    categoriesData?.categories;
  const categoriesArray: TransformedWPCategory[] = categoriesById
    ? Object.values(categoriesById)
    : [];

  const categoryTree = useMemo(
    () => buildCategoryTree(categoriesArray),
    [categoriesArray]
  );

  const rootCategoriesWithProducts = useMemo(
    () => categoryTree.filter(hasCategoryProducts),
    [categoryTree]
  );

  const currentChildren = useMemo(() => {
    if (!hoveredParentId) return [];
    const parent = rootCategoriesWithProducts.find(
      (cat) => cat.id === hoveredParentId
    );
    return parent?.children?.filter(hasCategoryProducts) || [];
  }, [hoveredParentId, rootCategoriesWithProducts]);

  // --- Render Logic ---

  // NOTE: We no longer return null based on isMegaMenuOpen here.
  // The visibility is controlled by the CSS class applied via main.ts listener.
  // This ensures the component stays mounted for mouseenter/leave events on its root.

  const renderCategoryList = (
    categories: CategoryWithChildren[],
    isParentList: boolean = false
  ) => (
    <ul
      className={`mega-menu__list ${
        isParentList ? "mega-menu__list--parents" : "mega-menu__list--children"
      }`}
    >
      {categories.map((category) => (
        <li
          key={category.id}
          className="mega-menu__item"
          onMouseEnter={
            isParentList ? () => setHoveredParentId(category.id) : undefined
          }
          // Optional: Reset hoveredParentId on mouse leave from the parent list itself?
          // onMouseLeave={isParentList ? () => setHoveredParentId(null) : undefined}
        >
          <a href={category.link} className="mega-menu__link">
            {category.name}
          </a>
          {/* Arrow for parent items with children that have products */}
          {isParentList && category.children?.some(hasCategoryProducts) && (
            <span className="mega-menu__arrow">›</span>
          )}
        </li>
      ))}
    </ul>
  );

  const renderSkeletons = (count = 8) => (
    <ul className="mega-menu__list mega-menu__list--parents">
      {[...Array(count)].map((_, index) => (
        <li
          key={`skel-${index}`}
          className="mega-menu__item mega-menu__item--skeleton"
        >
          <Skeleton height={20} width={`${Math.random() * 30 + 50}%`} />
        </li>
      ))}
    </ul>
  );

  // Note: We add the 'is-open' class dynamically in main.ts based on Redux state
  // The component itself doesn't need conditional rendering based on isMegaMenuOpen
  // But we still render the content conditionally based on loading state
  return (
    <div
      className="category-mega-menu" // Base class, 'is-open' added externally
      aria-hidden={!isMegaMenuOpen} // Set aria-hidden based on Redux state
      // MouseLeave handler will be attached to the *root* element (#category-mega-menu-root) in main.ts
    >
      <div className="mega-menu__panel mega-menu__panel--parents">
        {categoriesLoading
          ? renderSkeletons()
          : renderCategoryList(rootCategoriesWithProducts, true)}
      </div>
      {/* Only render children panel if there are children and a parent is hovered */}
      {currentChildren.length > 0 && (
        <div className="mega-menu__panel mega-menu__panel--children">
          {renderCategoryList(currentChildren)}
        </div>
      )}
      {/* --- REMOVED EMPTY PLACEHOLDER BLOCK --- */}
    </div>
  );
};

export default CategoryMegaMenu;
